import React from 'react';
import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import FuseAnimate from '@fuse/core/FuseAnimate/FuseAnimate';

// The FusePageSimple header forces a `mainThemeDark` ThemeProvider around its
// content, so the local `theme` here is always dark. To match the GlobalTabs
// card (which renders under the app's real theme), we derive colors from the
// app's actual main theme, passed in as style props.
const useStyles = makeStyles(() => ({
    headerContainer: {
        display: 'flex',
        flex: 1,
        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        minHeight: 64,
        marginBottom: 16,
        boxSizing: 'border-box',
        backgroundColor: ({ isDark }) => (isDark ? '#1F2937' : '#F3F4F6'),
        // `pointy` squares off the corners so the heading sits flush inside pages
        // that still have a square white card background.
        borderRadius: ({ pointy }) => (pointy ? 0 : 12),
        flexWrap: 'wrap',
        gap: 16,
        '@media (max-width:599.95px)': {
            flexDirection: 'column',
            alignItems: 'stretch'
        }
    },
    leftSection: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        flexShrink: 0
    },
    icon: {
        color: ({ textPrimary }) => textPrimary
    },
    titleWrap: {
        display: 'flex',
        flexDirection: 'column'
    },
    pageTitle: {
        fontSize: '1.625rem',
        fontWeight: 500,
        letterSpacing: '-0.01em',
        color: ({ textPrimary }) => textPrimary,
        lineHeight: 1.15
    },
    subtitle: {
        fontSize: '0.875rem',
        fontWeight: 400,
        color: ({ textSecondary }) => textSecondary,
        marginTop: 2
    },
    // Center area — grows to fill the space between the left (title) and right
    // (actions) areas. Good for a search field, date range, or filters.
    middleSection: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        flex: '1 1 auto',
        minWidth: 0,
        flexWrap: 'wrap'
    },
    rightSection: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        flexShrink: 0
    }
}));

/**
 * PageHeading - A reusable page heading with an icon, title, optional subtitle,
 * and optional left / middle / right content areas (search fields, buttons,
 * filters, date pickers, etc). Built to match the header pattern used across the
 * customer-details sidebar pages.
 *
 * Layout:  [ icon  title  {leftContent} ]   [ middleContent ]   [ rightContent ]
 *
 * @param {React.ElementType|React.ReactElement} icon - An icon component (e.g. EmailIcon)
 *        or an already-rendered icon element. Component references are rendered at fontSize="large".
 * @param {string|React.ReactNode} title - The page title.
 * @param {string|React.ReactNode} [subtitle] - Optional secondary line under the title.
 * @param {React.ReactNode} [leftContent] - Optional content in the left area, right after the title.
 * @param {React.ReactNode} [middleContent] - Optional content in the centre area (grows to fill space).
 * @param {React.ReactNode} [rightContent] - Optional content in the right area (buttons, export, filters).
 * @param {React.ReactNode} [actions] - Alias for `rightContent` (kept for backward compatibility).
 * @param {React.ReactNode} [children] - Optional extra content appended to the left area.
 * @param {boolean} [animate=true] - Whether to wrap the icon/title in FuseAnimate transitions.
 * @param {boolean} [pointy=false] - Square off the corners (no border-radius). Use on pages that
 *        still have a square white card background so the heading sits flush.
 * @param {string} [className] - Optional additional class for the outer container.
 */
function PageHeading({
    icon,
    title,
    subtitle,
    leftContent,
    middleContent,
    rightContent,
    actions,
    children,
    animate = true,
    pointy = false,
    className = ''
}) {
    // Match the app's real (content) theme, not the forced-dark header theme,
    // so the background/text line up with the GlobalTabs card below.
    const mainTheme = useSelector(({ fuse }) => fuse.settings.mainTheme);
    const isDark = mainTheme?.palette?.type === 'dark';
    const classes = useStyles({
        isDark,
        pointy,
        textPrimary: mainTheme?.palette?.text?.primary || (isDark ? '#fff' : 'rgba(0,0,0,0.87)'),
        textSecondary: mainTheme?.palette?.text?.secondary || (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)')
    });

    const renderIcon = () => {
        if (!icon) return null;
        if (React.isValidElement(icon)) return icon;
        const IconComponent = icon;
        return <IconComponent fontSize="large" className={classes.icon} />;
    };

    const iconNode = renderIcon();
    // `actions` is the legacy name for the right area.
    const right = rightContent || actions;

    const titleBlock = (title || subtitle) && (
        <div className={classes.titleWrap}>
            {title && <Typography className={classes.pageTitle}>{title}</Typography>}
            {subtitle && <Typography className={classes.subtitle}>{subtitle}</Typography>}
        </div>
    );

    return (
        <div className={`${classes.headerContainer} ${className}`}>
            <div className={classes.leftSection}>
                {iconNode && (
                    animate ? (
                        <FuseAnimate animation="transition.expandIn" delay={300}>
                            {iconNode}
                        </FuseAnimate>
                    ) : (
                        iconNode
                    )
                )}

                {titleBlock && (
                    animate ? (
                        <FuseAnimate animation="transition.slideLeftIn" delay={300}>
                            {titleBlock}
                        </FuseAnimate>
                    ) : (
                        titleBlock
                    )
                )}

                {leftContent}
                {children}
            </div>

            {middleContent && <div className={classes.middleSection}>{middleContent}</div>}

            {right && <div className={classes.rightSection}>{right}</div>}
        </div>
    );
}

export default PageHeading;
